<template>
  <div class="schedule">
    <div class="header">
      <div class="day-header__title">
        <span>
          {{ getScheduleDayHeader(dayIndex, diet?.data.start_date).name }}
          {{ getScheduleDayHeader(dayIndex, diet?.data.start_date).date ?? '' }}
        </span>
      </div>
      <div class="day-menu">
        <IconButton
          name="more_vert"
          variant="secondary"
          @click.stop="setActiveMenu(`day-menu-${dayIndex}`)"
        />
        <div v-if="checkActiveMenu(`day-menu-${dayIndex}`)" class="day-menu__items">
          <div class="day-menu__item" @click.once="copyDayHandler()">
            <BaseIcon name="filter_none" size="16" />
            <span>Skopiuj dzień</span>
          </div>
          <div
            v-if="canDietBeEdited"
            class="day-menu__item"
            :class="{ disabled: !copiedDayStatus }"
            @click.once="pasteDayHandler()"
          >
            <BaseIcon name="queue" size="16" :type="copiedDayStatus ? 'default' : 'disabled'" />
            <span>Wklej dzień</span>
          </div>
          <div
            v-if="canDietBeEdited"
            class="day-menu__item"
            @click.once="setActiveDayDeleteModal(dayIndex)"
          >
            <BaseIcon name="delete" size="16" />
            <span>Wyczyść dzień</span>
          </div>
        </div>
        <BaseModal v-if="activeDayDeleteModal === dayIndex" @close="setActiveDayDeleteModal()">
          <template #title>Czy na pewno chcesz wyczyścić dzień?</template>
          Wyczyszczenie dnia usunie aktualnie dodane potrawy w kolumnie
          <span>
            {{ getDayDisplayName(dayIndex, diet?.data.diet_type, diet?.data.start_date) }}
          </span>
          .
          <template #actions>
            <StyledButton variant="outline" @click.once="setActiveDayDeleteModal()">
              Anuluj
            </StyledButton>
            <StyledButton @click.once="setActiveDayDeleteModal(), deleteDayHandler()">
              Wyczyść
            </StyledButton>
          </template>
        </BaseModal>
      </div>
    </div>
    <div
      v-for="(meal, mealIndex) in meals"
      :key="`meal-${mealIndex}`"
      class="meal"
      :class="{
        'is-disabled': !canDietBeEdited
      }"
      @dragover.prevent
      @drop="dropDishOrIngredientHandler($event, mealIndex)"
    >
      <div class="meal-header">
        <BaseSelect
          class="meal-hour"
          :model-value="meal.days[dayIndex]?.hour ?? ''"
          :options="hourOptions"
          @update:model-value="updateDayHourHandler($event, mealIndex)"
        />
        <span class="meal-name">{{ meal.name }}</span>
        <Tooltip
          v-if="
            schedule?.energyDistribution &&
              (meal.days[dayIndex]?.dishesAndIngredients?.length ?? 0 > 0)
          "
          placement="top-end"
        >
          <template #trigger>
            <div class="energy-distribution">
              <div
                class="energy-distribution__dot"
                :class="meal.days[dayIndex]?.energyDistributionClass"
              />
            </div>
          </template>
          <div>
            <div class="energy-distribution__title">Rozkład energii</div>
            <div class="energy-distribution__data">
              {{ meal.days[dayIndex]?.energyDistribution }}
              %
              <span class="energy-distribution__data-target">
                ({{ meal.energyDistribution }}
                %)
              </span>
              - {{ meal.days[dayIndex]?.calories }} kcal
              <span class="energy-distribution__data-target">
                ({{ meal.energyDistributionCalories }}
                kcal)
              </span>
            </div>
          </div>
        </Tooltip>
      </div>
      <div
        class="meal-data"
        :class="{ empty: meal.days[dayIndex]?.dishesAndIngredients.length === 0 }"
      >
        <div class="meal-dishes-and-ingredients">
          <div
            v-for="(dishOrIngredient, dishOrIngredientIndex) in meal.days[dayIndex]
              ?.dishesAndIngredients ?? []"
            :key="`dish-or-ingredient-${dishOrIngredientIndex}`"
            class="dish-or-ingredient"
            :class="{
              active: activeDishOrIngredient(mealIndex, dishOrIngredientIndex),
              repeated: dishOrIngredient.repeated
            }"
            :draggable="true ? canDietBeEdited : false"
            @click="clickDishOrIngredient(mealIndex, dishOrIngredientIndex)"
            @dragstart="dragStartDishOrIngredientHandler($event, mealIndex, dishOrIngredientIndex)"
            @mousedown.ctrl="updateDragMode('copy')"
          >
            <div class="dish-or-ingredient__name">
              {{ dishOrIngredient.name }}
              <span v-if="dishOrIngredient.portions">{{ dishOrIngredient.portions }}</span>
              <DishOrIngredientAllergens
                test-id="DietDetailDaySchedule"
                :dish-or-ingredient="dishOrIngredient"
              />
            </div>
            <div v-if="canDietBeEdited" class="dish-or-ingredient__menu">
              <Tooltip
                v-if="repeatedDishesAndIngredientsActive && dishOrIngredient.repeated"
                placement="top"
              >
                <template #trigger>
                  <IconButton
                    class="dish-or-ingredient__menu-item"
                    icon-size="16"
                    name="assignment"
                    @click.stop="
                      showRepeatedDishDietsModal(dishOrIngredient.name, dishOrIngredient.type)
                    "
                  />
                </template>
                <div>
                  Wyświetl
                  <span v-if="dishOrIngredient.type == 'dish'">powtarzającą się potrawę</span>
                  <span v-else>powtarzający się produkt</span>
                </div>
              </Tooltip>
              <Tooltip placement="top">
                <template #trigger>
                  <IconButton
                    class="dish-or-ingredient__menu-item"
                    icon-size="14"
                    name="filter_none"
                    @click.stop="copyDishOrIngredientHandler(mealIndex, dishOrIngredientIndex)"
                    @mousedown="updateDragMode('copy')"
                  />
                </template>
                <div>W celu skopiowania kliknij w ikonę lub przeciągnij posiłek</div>
              </Tooltip>
              <Tooltip placement="top">
                <template #trigger>
                  <IconButton
                    class="dish-or-ingredient__menu-item"
                    icon-size="16"
                    name="delete"
                    type="destructive"
                    variant="outline"
                    @click.stop="deleteDishOrIngredientHandler(mealIndex, dishOrIngredientIndex)"
                  />
                </template>
                <div>W celu usunięcia posiłku kliknij w ikonę</div>
              </Tooltip>
            </div>
          </div>
          <div class="add-dish-or-ingredient">
            <StyledButton
              v-if="!checkActiveAddDishOrIngredient(`add-dish-or-ingredient-${mealIndex}`)"
              class="add-dish-or-ingredient__button base-button--secondary base-button--outline"
              left-icon="add"
              :data-component="`add-dish-or-ingredient-${mealIndex}`"
              @click.stop="setActiveAddDishOrIngredient(`add-dish-or-ingredient-${mealIndex}`)"
              @keyup.enter="setActiveAddDishOrIngredient(`add-dish-or-ingredient-${mealIndex}`)"
            >
              Dodaj pozycję
            </StyledButton>
            <DishOrIngredientAddInput
              v-else
              mode="all"
              test-id="DietDetailDaySchedule"
              :dish-meal-type="meal.type"
              :filters="dishFlags"
              :related-dishes="relatedDishes"
              @close="setActiveAddDishOrIngredient()"
              @keyup.esc="setActiveAddDishOrIngredient()"
              @select="
                addDishOrIngredient(
                  $event.dishOrIngredientId,
                  $event.dishOrIngredientType,
                  mealIndex
                )
              "
              @select-unused="
                addUnusedDishPortion(
                  $event.mealIndex,
                  $event.dayIndex,
                  $event.dishOrIngredientIndex,
                  mealIndex
                )
              "
            />
          </div>
        </div>
        <DietDetailDayScheduleNutrients :day="meal.days[dayIndex]" />
      </div>
    </div>
    <RepeatedDishDietsModal
      v-if="repeatedDishDietsModal && repeatedDishDataType === 'dish' && diet"
      :current-diet-active="diet.data.repeated_current_diet_active ?? true"
      :dish-name="repeatedDishDataName"
      :meals="diet.data.meals"
      :previous-diets-active="diet.data.repeated_previous_diets_active ?? true"
      :repeated-diets="repeatedDishDietsData ?? []"
      @close="hideRepeatedDishDietsModal"
    />
    <BaseModal v-else-if="repeatedDishDietsModal" @close="hideRepeatedDishDietsModal">
      <template #title>
        <span v-if="repeatedDishDataType == 'dish'">Powtarzająca się potrawa</span>
        <span v-else>Powtarzający się produkt</span>
      </template>
      <div class="doi-name">{{ repeatedDishDataName }}</div>
      <div class="doi-description">
        <span v-if="repeatedDishDataType == 'dish'">Ta potrawa</span>
        <span v-else>Ten produkt</span>
        występuje w następujących jadłospisach pacjenta (opcja uwzględnia do 5 jadłospisów wstecz):
      </div>
      <div
        v-for="repeatedDiet in repeatedDishDietsData"
        :key="`diet-${repeatedDiet.id}`"
        class="repeated-dish-diets-row"
      >
        <a target="_blank" :href="repeatedDiet.url">
          {{ repeatedDiet.name }}
          - {{ repeatedDiet.start_date }} - {{ repeatedDiet.end_date }} (liczba wystąpień:
          {{ repeatedDiet.number_of_occurences }}
          )
        </a>
      </div>
    </BaseModal>
  </div>
</template>

<script lang="ts" setup>
  import { S } from '@mobily/ts-belt'
  import { useEventListener } from '@vueuse/core'
  import { storeToRefs } from 'pinia'
  import { computed, ref } from 'vue'
  import { useRoute } from 'vue-router'

  import BaseIcon from '@/components/BaseIcon.vue'
  import BaseModal from '@/components/BaseModal.vue'
  import BaseSelect from '@/components/BaseSelect.vue'
  import DishOrIngredientAddInput from '@/components/DishOrIngredientAddInput.vue'
  import DietDetailDayScheduleNutrients from '@/components/diets/DietDetailDayScheduleNutrients.vue'
  import DishOrIngredientAllergens from '@/components/diets/DishOrIngredientAllergens/DishOrIngredientAllergens.vue'
  import RepeatedDishDietsModal from '@/components/diets/RepeatedDishDietsModal.vue'
  import { IconButton } from '@/components/IconButton'
  import { StyledButton } from '@/components/StyledButton'
  import { Tooltip } from '@/components/ui'
  import { getDayDisplayName, getScheduleDayHeader, hours } from '@/const'
  import eventBus from '@/eventBus'
  import { dietsService } from '@/services/dietsService'
  import { useDietsStore } from '@/store/dietsStore'
  import { useGlobalStore } from '@/store/globalStore'
  import { reportError } from '@/utils/reportError'

  import type { DragMode } from '@/store/types'

  const dietStore = useDietsStore()
  const {
    diet,
    schedule,
    copiedDayStatus,
    dragMode,
    details,
    relatedDishes,
    repeatedDishesAndIngredientsActive,
    canDietBeEdited,
  } = storeToRefs(dietStore)
  const globalStore = useGlobalStore()
  const route = useRoute()

  const activeMenu = ref()
  const activeAddDishOrIngredient = ref()
  const activeDayDeleteModal = ref()
  const repeatedDishDietsModal = ref(false)
  const repeatedDishDietsData = ref()
  const user = computed(() => globalStore.user)
  const repeatedDishDataName = ref('')
  const repeatedDishDataType = ref('')

  const dayIndex = computed(() => parseInt(route.params.dayIndex as unknown as string))
  const dishFlags = computed(() => {
    return diet.value?.data.dish_search_filters
  })
  const meals = computed(() => {
    return schedule.value?.meals ?? []
  })

  const hourOptions = computed(() => {
    return Array.from(hours, hour => {
      return { text: hour, value: hour }
    })
  })

  const showRepeatedDishDietsModal = async (doiName: string, doiType: string) => {
    try {
      const response = await dietsService.fetchPatientDietRepeatedDishDiets(
        route.params.dietId as unknown as number,
        doiName,
        diet.value?.data.repeated_diets_count ?? 5,
      )
      repeatedDishDietsModal.value = true
      repeatedDishDietsData.value = response.data
      repeatedDishDataName.value = doiName
      repeatedDishDataType.value = doiType
    } catch (err) {
      reportError(err, 'Error while fetching repeated dish diets', {
        dietId: route.params.dietId,
        doiName: doiName,
      })
    }
  }

  const hideRepeatedDishDietsModal = () => {
    repeatedDishDietsModal.value = false
    repeatedDishDietsData.value = []
    repeatedDishDataName.value = ''
    repeatedDishDataType.value = ''
  }
  const setActiveMenu = (value?: string) => {
    activeAddDishOrIngredient.value = undefined
    if (activeMenu.value === value) {
      activeMenu.value = undefined
    } else {
      activeMenu.value = value
    }
  }
  const checkActiveMenu = (value: string) => {
    return activeMenu.value === value
  }

  const updateDragMode = (v: DragMode) => {
    dietStore.updateDragMode(v)
  }

  const copyDayHandler = () => {
    dietStore.copyDay(dayIndex.value)
  }
  const pasteDayHandler = () => {
    dietStore.pasteDay(dayIndex.value)
  }
  const deleteDayHandler = () => {
    dietStore.deleteDay(dayIndex.value)
  }
  const updateDayHourHandler = (dayHour: string, mealIndex: number) => {
    dietStore.updateDayHour(mealIndex, dayIndex.value, dayHour)
  }
  const setActiveAddDishOrIngredient = (value?: string) => {
    activeMenu.value = undefined
    activeAddDishOrIngredient.value = value
  }
  const checkActiveAddDishOrIngredient = (value: string) => {
    return activeAddDishOrIngredient.value === value
  }
  const deleteDishOrIngredientHandler = (_mealIndex: number, _dishOrIngredientIndex: number) => {
    dietStore.deleteDishOrIngredient(_mealIndex, dayIndex.value, _dishOrIngredientIndex)
  }
  const setActiveDayDeleteModal = (value?: number) => {
    activeDayDeleteModal.value = value
  }
  const addDishOrIngredient = async (
    id: number,
    type: 'dish' | 'ingredient',
    mealIndex: number,
  ) => {
    try {
      await dietStore.fetchAndAddDishOrIngredient({
        dayIndex: dayIndex.value,
        id,
        mealIndex,
        type,
      })

      setActiveAddDishOrIngredient()
    } catch (err) {
      reportError(err, 'Error while fetching and adding dish or ingredient', {
        dayIndex: dayIndex.value,
        id,
        mealIndex,
        type,
      })
    }
  }
  const addUnusedDishPortion = (
    eventMealIndex: number,
    eventDayIndex: number,
    dishOrIngredientIndex: number,
    targetMealIndex: number,
  ) => {
    dietStore.copyDishOrIngredient(
      targetMealIndex,
      dayIndex.value,
      eventMealIndex,
      eventDayIndex,
      dishOrIngredientIndex,
      true,
    )
  }
  const dragStartDishOrIngredientHandler = (
    event: DragEvent,
    mealIndex: number,
    dishOrIngredientIndex: number,
  ) => {
    if (canDietBeEdited.value && event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copyMove'
      event.dataTransfer.dropEffect = dragMode.value as DataTransfer['dropEffect']
      event.dataTransfer.setData('mealIndex', mealIndex.toString())
      event.dataTransfer.setData('dishOrIngredientIndex', dishOrIngredientIndex.toString())
    }
  }
  const copyDishOrIngredientHandler = (mealIndex: number, dishOrIngredientIndex: number) => {
    dietStore.copyDishOrIngredient(
      mealIndex,
      dayIndex.value,
      mealIndex,
      dayIndex.value,
      dishOrIngredientIndex,
    )
    updateDragMode('move')
  }

  const handleDragAddDrop = async (dataTransfer: DataTransfer, _mealIndex: number) => {
    const id = parseInt(dataTransfer.getData('dishOrIngredientId'))
    if (Number.isNaN(id)) {
      return
    }
    const type = dataTransfer.getData('type') as 'dish' | 'ingredient'
    if (!['dish', 'ingredient'].includes(type)) {
      return
    }
    const dishOrIngredientName = dataTransfer.getData('dishOrIngredientName')
    if (S.isEmpty(dishOrIngredientName)) {
      return
    }
    await dietStore.fetchAndAddDishOrIngredient({
      dayIndex: dayIndex.value,
      id,
      mealIndex: _mealIndex,
      type,
    })
    if (type === 'ingredient') {
      await dietStore.sendProductToFile({
        email: user.value?.email ?? '',
        productId: id,
        productName: dishOrIngredientName,
      })
    }
  }

  const handleDragCopyDrop = (dataTransfer: DataTransfer, _mealIndex: number) => {
    const mealIndex = parseInt(dataTransfer.getData('mealIndex'))
    const dishOrIngredientIndex = parseInt(dataTransfer.getData('dishOrIngredientIndex'))

    if (Number.isNaN(mealIndex) || Number.isNaN(dishOrIngredientIndex)) {
      return
    }
    dietStore.copyDishOrIngredient(
      _mealIndex,
      dayIndex.value,
      mealIndex,
      dayIndex.value,
      dishOrIngredientIndex,
    )
  }

  const handleDragMoveDrop = (dataTransfer: DataTransfer, _mealIndex: number) => {
    const mealIndex = parseInt(dataTransfer.getData('mealIndex'))
    const dishOrIngredientIndex = parseInt(dataTransfer.getData('dishOrIngredientIndex'))

    if (Number.isNaN(mealIndex) || Number.isNaN(dishOrIngredientIndex)) {
      return
    }

    dietStore.moveDishOrIngredient(
      _mealIndex,
      dayIndex.value,
      mealIndex,
      dayIndex.value,
      dishOrIngredientIndex,
    )
  }

  const dropDishOrIngredientHandler = async (event: DragEvent, _mealIndex: number) => {
    try {
      if (canDietBeEdited.value && event.dataTransfer) {
        event.preventDefault()
        if (dragMode.value === 'add') {
          await handleDragAddDrop(event.dataTransfer, _mealIndex)
        } else if (dragMode.value === 'copy') {
          handleDragCopyDrop(event.dataTransfer, _mealIndex)
        } else {
          handleDragMoveDrop(event.dataTransfer, _mealIndex)
        }
        updateDragMode('move')
      }
    } catch (err) {
      reportError(err, 'Error while dropping dish or ingredient', {
        dayIndex: dayIndex.value,
        id: event.dataTransfer?.getData('dishOrIngredientId'),
        mealIndex: _mealIndex,
      })
    }
  }
  const activeDishOrIngredient = (mealIndex: number, dishOrIngredientIndex: number) => {
    if (
      details.value?.mealIndex === mealIndex &&
      details.value.dayIndex === dayIndex.value &&
      details.value.dishOrIngredientIndex === dishOrIngredientIndex
    ) {
      return true
    }
    return false
  }
  const clickDishOrIngredient = (mealIndex: number, dishOrIngredientIndex: number) => {
    if (activeDishOrIngredient(mealIndex, dishOrIngredientIndex)) {
      dietStore.updateDetails()
    } else {
      dietStore.updateDetails({
        dayIndex: dayIndex.value,
        dishOrIngredientIndex: dishOrIngredientIndex,
        mealIndex: mealIndex,
      })
    }
    eventBus.emit('resizeBaseTextArea')
  }

  const hideMenu = () => {
    activeMenu.value = undefined
    activeAddDishOrIngredient.value = undefined
  }

  useEventListener('click', hideMenu)
</script>

<style scoped>
  .header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 42px;
    background: var(--color-main-100);
  }

  .day-header__title {
    display: flex;
    flex-direction: row;
    font-family: Montserrat-Medium, sans-serif;
    color: #fff;
    text-align: center;
  }

  .day-header__date {
    color: rgb(255 255 255 / 80%);
  }

  /* Day menu */
  .day-menu {
    position: absolute;
    top: 0;
    right: 7px;
    bottom: 0;
    display: flex;
    align-items: center;
    padding: 0;
    text-decoration: none;
    background: transparent;
    border: 0;
    border-radius: 16px;
    transition: all 0.2s ease;
  }

  .day-menu__items {
    position: absolute;
    top: 35px;
    right: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    background: #fff;
    box-shadow: 0 1px 6px 0 rgb(155 155 155 / 50%);
  }

  .day-menu__item {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: left;
    height: 20px;
    padding: 3px 6px;
    font-size: 12px;
    white-space: nowrap;
    cursor: pointer;
  }

  .day-menu__item:hover {
    background: #f8f8f8;
  }

  .day-menu__item.disabled {
    color: var(--color-disabled-text);
    pointer-events: none;
  }

  .day-menu__item.disabled .base-icon {
    color: var(--color-disabled-text);
  }

  .day-menu__item span {
    margin-left: 6px;
  }

  .meal-header {
    position: relative;
    height: 32px;
    margin: 0 5px;
    text-align: center;
    background: #f5f5f5;
  }

  .meal-name {
    font-family: Montserrat-SemiBold;
    font-size: 14px;
    line-height: 32px;
    color: var(--color-primary-text);
    letter-spacing: 1.41px;
  }

  /* Meal Hour */
  .meal-hour {
    position: absolute;
    left: 0;
  }

  .meal.is-disabled .meal-hour :deep(.base-select__selected) {
    pointer-events: none;
  }

  .meal.is-disabled:hover .meal-hour :deep(.base-select__selected) {
    border-bottom: none;
  }

  .meal-hour :deep(.base-select__selected) {
    padding: 9px 21px 8px 8px;
    line-height: 14px;
    border-bottom: 1px solid transparent;
  }

  .meal.is-disabled .base-select {
    cursor: text;
  }

  .meal-hour :deep(.base-select__selected::after),
  .meal.is-disabled:hover .meal-hour :deep(.base-select__selected::after),
  .meal.is-disabled .add-dish-or-ingredient button {
    display: none;
  }

  .meal:hover .meal-hour :deep(.base-select__selected) {
    border-bottom: 1px solid var(--color-tertiary-text);
  }

  .meal:hover .meal-hour :deep(.base-select__selected:hover) {
    border-bottom: 1px solid var(--color-primary-text);
  }

  .meal:hover .meal-hour :deep(.base-select__selected::after),
  .meal-hour :deep(.base-select__selected--active::after) {
    display: block;
  }

  .meal-hour :deep(.base-select__selected--active),
  .meal:hover .meal-hour :deep(.base-select__selected--active) {
    border-bottom: 1px solid var(--color-main-100);
  }

  /* Energy distribution */
  .energy-distribution {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 8px;
    height: 8px;
  }

  .energy-distribution__dot {
    width: 8px;
    height: 8px;
    border-radius: 4px;
  }

  .energy-distribution__dot.green {
    background: var(--color-success-600);
  }

  .energy-distribution__dot.yellow {
    background: var(--color-warning-100);
  }

  .energy-distribution__dot.red {
    background: var(--color-destructive-100);
  }

  .energy-distribution__title {
    margin-bottom: 4px;
  }

  .energy-distribution__data {
    font-family: Montserrat-Medium;
  }

  .energy-distribution__data-target {
    color: var(--color-tertiary-text);
  }

  /* Meal */
  .meal-data {
    display: flex;
    flex-direction: row;
    min-height: 80px;
    padding: 9px 13px;
  }

  .meal-data.empty {
    align-items: center;
    min-height: 52px;
  }

  .meal-dishes-and-ingredients {
    flex-grow: 1;
    margin-top: 25px;
  }

  .meal-data.empty .meal-dishes-and-ingredients {
    margin: 0;
  }

  /* Dish or ingredient */
  .dish-or-ingredient {
    position: relative;
    display: flex;
    flex-direction: row;
    min-height: 16px;
    padding: 8px 15px 8px 5px;
    margin-bottom: 8px;
    cursor: pointer;
    background: var(--color-main-10);
    border-radius: 4px;
  }

  .dish-or-ingredient.active {
    border-left: 4px solid #ec5787;
  }

  .dish-or-ingredient.repeated {
    background: var(--color-warning-30);
  }

  .dish-or-ingredient.repeated .dish-or-ingredient__copy-button:hover,
  .dish-or-ingredient.repeated .dish-or-ingredient__assignment-button:hover {
    background: var(--color-warning-60);
  }

  .dish-or-ingredient.repeated .dish-or-ingredient__copy-button .base-icon,
  .dish-or-ingredient.repeated .dish-or-ingredient__assignment-button .base-icon {
    color: var(--color-warning-100);
  }

  .dish-or-ingredient.repeated .dish-or-ingredient__delete-button .base-icon {
    color: var(--color-warning-100);
  }

  .dish-or-ingredient.repeated .dish-or-ingredient__delete-button:hover .base-icon {
    color: #f55f6d;
  }

  .dish-or-ingredient.repeated.active {
    border-left: 4px solid var(--color-warning-100);
  }

  .dish-or-ingredient__name {
    display: inline-flex;
    flex-grow: 1;
    gap: 8px;
    align-items: center;
    font-size: 14px;
    line-height: 16px;
    overflow-wrap: break-word;
  }

  .dish-or-ingredient__name span {
    font-family: Montserrat-Bold;
    font-size: 14px;
    color: var(--color-main-100);
  }

  .dish-or-ingredient.repeated .dish-or-ingredient__name span {
    color: var(--color-warning-100);
  }

  .dish-or-ingredient__menu {
    position: absolute;
    top: 0;
    right: 2px;
    bottom: 0;
    display: none;
    flex-direction: row;
    padding-left: 40px;
    background: linear-gradient(to right, rgb(255 255 255 / 0%), var(--color-main-10) 35%);
  }

  .dish-or-ingredient.repeated .dish-or-ingredient__menu {
    background: linear-gradient(to right, rgb(255 255 255 / 0%), var(--color-warning-30) 35%);
  }

  .dish-or-ingredient__menu-item {
    position: relative;
    top: calc(50% - 14px);
  }

  .dish-or-ingredient:hover .dish-or-ingredient__menu {
    display: flex;
  }

  /* Add dish or ingradient */
  .add-dish-or-ingredient {
    height: 30px;
  }

  .add-dish-or-ingredient__button {
    display: none;
  }

  .meal:hover .add-dish-or-ingredient__button {
    display: block;
  }

  .doi-name {
    margin-bottom: 13px;
    font-size: 16px;
    color: var(--color-primary-text);
  }

  .doi-description {
    font-size: 12px;
    color: var(--color-primary-text);
  }

  .repeated-dish-diets-row a {
    display: flex;
    margin-top: 10px;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-main-100);
    text-decoration: none;
  }

  .repeated-dish-diets-row a:hover {
    color: var(--color-main-110);
    text-decoration: underline;
  }
</style>
